import { useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { salvarFoto } from "../../lib/foto";
import { dataHora, diasAtras } from "../../lib/format";
import { TendenciaLabel } from "../../ui/Tendencia";
import { calcularDerivadosAtividade, statusSugeridoAtividade } from "../domain";
import { fraseParticipacao } from "../evolucao";
import {
  arquivarAtividade,
  definirStatusAtividade,
  editarAtividade,
  getAtividade,
  getMomentosDaAtividade,
} from "../queries";
import {
  AJUDA_LABEL,
  AUTONOMIA_LABEL,
  CONTEXTO_LABEL,
  DEMANDA_LABEL,
  ESTADO_REGULACAO_LABEL,
  FATOR_LABEL,
  INTENSIDADE_LABEL,
  ORIGEM_LABEL,
  PARTICIPACAO_LABEL,
  PREVISIBILIDADE_LABEL,
  SISTEMA_LABEL,
  STATUS_ATIVIDADE_LABEL,
  TIPO_LABEL,
  type MomentoPsicomotor,
  type StatusAtividade,
} from "../types";
import { AtividadeFoto } from "../ui/AtividadeFoto";
import { StatusAtividadeBadge } from "../ui/StatusAtividadeBadge";
import { ParticipacaoTimeline } from "../ui/ParticipacaoTimeline";
import { EfeitoBadge } from "../ui/EfeitoBadge";

const ORDEM_STATUS: StatusAtividade[] = [
  "favorita",
  "em_exploracao",
  "nao_usada",
  "sensivel",
];

export function DetalheAtividade() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const atividade = useLiveQuery(() => getAtividade(id), [id]);
  const momentos = useLiveQuery(() => getMomentosDaAtividade(id), [id]) ?? [];

  if (!atividade) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>
          ← voltar
        </button>
        <div className="vazio">atividade não encontrada.</div>
      </main>
    );
  }

  const d = calcularDerivadosAtividade(id, momentos);
  const sugerido = statusSugeridoAtividade(atividade.status, d);
  const psm = atividade.psicomotor;
  const chipsPsm = [
    psm.fator_alvo && FATOR_LABEL[psm.fator_alvo],
    ...psm.sistema.map((s) => SISTEMA_LABEL[s]),
    psm.intensidade && INTENSIDADE_LABEL[psm.intensidade],
    psm.previsibilidade && `previsib.: ${PREVISIBILIDADE_LABEL[psm.previsibilidade]}`,
    atividade.demanda_relacional && DEMANDA_LABEL[atividade.demanda_relacional],
  ].filter(Boolean) as string[];

  async function anexarArquivo(file: File | undefined) {
    if (!file || !atividade) return;
    const ref = await salvarFoto(file);
    await editarAtividade(id, { midias: [...atividade.midias, ref] });
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav(-1)}>
        ← voltar
      </button>

      <div className="row" style={{ marginBottom: "var(--s4)" }}>
        <label className="foto-editavel" title="adicionar foto">
          <AtividadeFoto midias={atividade.midias} nome={atividade.nome} tipo={atividade.tipo} />
          <span className="foto-editavel-cam" aria-hidden>📷</span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              void anexarArquivo(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </label>
        <div className="crescer">
          <h1 className="tela-titulo" style={{ marginBottom: 4 }}>
            {atividade.nome}
          </h1>
          <div className="meta" style={{ color: "var(--text-muted)" }}>
            {TIPO_LABEL[atividade.tipo]}
          </div>
        </div>
        <StatusAtividadeBadge status={atividade.status} />
      </div>

      <label className="btn btn-fantasma btn-bloco" style={{ cursor: "pointer", marginBottom: "var(--s2)" }}>
        🎬 anexar clipe de vídeo
        <input
          type="file"
          accept="video/*"
          hidden
          onChange={(e) => {
            void anexarArquivo(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>

      <button
        className="btn btn-primario btn-bloco btn-grande"
        onClick={() => nav(`/gingado/registrar/${id}`)}
        style={{ marginBottom: "var(--s5)" }}
      >
        🤸 registrar momento
      </button>

      {atividade.seguranca && (
        <div className="callout-seguranca" style={{ marginBottom: "var(--s5)" }}>
          <span aria-hidden>⚠️</span> {atividade.seguranca}
        </div>
      )}

      {/* P0.4 — contador + evolução da participação + efeito regulatório + autonomia */}
      <section className="card">
        <div className="row-entre">
          <div>
            <div style={{ fontSize: "1.6rem", fontWeight: 500 }}>{d.n_momentos}</div>
            <div className="meta" style={{ color: "var(--text-muted)" }}>
              {d.n_momentos === 1 ? "momento" : "momentos"} · {diasAtras(d.dias_desde_ultimo)}
            </div>
          </div>
          <TendenciaLabel tendencia={d.tendencia} />
        </div>

        <div style={{ marginTop: "var(--s4)" }}>
          <ParticipacaoTimeline momentos={momentos} />
        </div>

        <p className="evol-frase">{fraseParticipacao(atividade.nome, momentos, d)}</p>

        <div className="efeito-linha">
          efeito tônico: <EfeitoBadge efeito={d.efeito_regulatorio} forte />
        </div>

        {d.autonomia !== "poucos_dados" && (
          <div className="meta" style={{ color: "var(--text-muted)", marginTop: 4 }}>
            {AUTONOMIA_LABEL[d.autonomia]}
          </div>
        )}

        {d.passo_maximo !== null && (
          <div className="meta" style={{ color: "var(--text-muted)", marginTop: 4 }}>
            maior passo: {PARTICIPACAO_LABEL[d.passo_maximo]}
          </div>
        )}
      </section>

      {/* status — o cuidador decide; o app só sugere */}
      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">situação</h2>
        <div className="card">
          <div className="status-opcoes">
            {ORDEM_STATUS.map((s) => (
              <button
                key={s}
                type="button"
                className={`status-opt${atividade.status === s ? " sel" : ""}`}
                aria-pressed={atividade.status === s}
                onClick={() => definirStatusAtividade(id, s)}
              >
                {STATUS_ATIVIDADE_LABEL[s]}
              </button>
            ))}
          </div>
          <p className="status-regra">
            <strong>favorita</strong> = funciona bem · <strong>em exploração</strong> =
            experimentando · <strong>não usada</strong> = ainda não registrou ·
            <strong> sensível</strong> = a criança se desregula/recusa ou há risco físico
            (um dado a respeitar, não um fracasso — fica fora do modo escolha).
          </p>
          {sugerido !== atividade.status && (
            <button
              className="btn btn-fantasma"
              style={{ marginTop: "var(--s2)" }}
              onClick={() => definirStatusAtividade(id, sugerido)}
            >
              sugestão pelos dados: {STATUS_ATIVIDADE_LABEL[sugerido]} — aplicar
            </button>
          )}
        </div>
      </section>

      {chipsPsm.length > 0 && (
        <section className="secao">
          <h2 className="secao-titulo">perfil psicomotor</h2>
          <div className="chips">
            {chipsPsm.map((c) => (
              <span className="chip" key={c}>
                {c}
              </span>
            ))}
          </div>
        </section>
      )}

      {atividade.observacoes && (
        <section className="secao">
          <h2 className="secao-titulo">observações</h2>
          <div className="card">{atividade.observacoes}</div>
        </section>
      )}

      {/* histórico */}
      <section className="secao">
        <h2 className="secao-titulo">histórico</h2>
        {momentos.length === 0 ? (
          <div className="card" style={{ color: "var(--text-muted)" }}>
            sem momentos ainda.
          </div>
        ) : (
          momentos.map((m) => (
            <div className="card" key={m.id}>
              <div className="row-entre">
                <span style={{ fontWeight: 500 }}>
                  {PARTICIPACAO_LABEL[m.passo_participacao]}
                </span>
                <span className="meta" style={{ color: "var(--text-muted)" }}>
                  {dataHora(m.data_hora)}
                </span>
              </div>
              <div className="meta" style={{ color: "var(--text-suave)", marginTop: 4 }}>
                {origemDescricao(m)}
                {ajudaDescricao(m)}
                {regulacaoDescricao(m)}
                {!m.confirmado && " · pendente"}
              </div>
            </div>
          ))
        )}
      </section>

      <button
        className="btn btn-bloco"
        style={{ marginTop: "var(--s4)", color: "var(--text-muted)" }}
        onClick={async () => {
          await arquivarAtividade(id);
          nav("/gingado/biblioteca", { replace: true });
        }}
      >
        arquivar atividade
      </button>
    </main>
  );
}

function origemDescricao(m: MomentoPsicomotor): string {
  const base = m.origem === "crianca" ? "escolha da criança" : ORIGEM_LABEL[m.origem];
  return m.contexto ? `${base} · ${CONTEXTO_LABEL[m.contexto]}` : base;
}

function ajudaDescricao(m: MomentoPsicomotor): string {
  return m.nivel_ajuda ? ` · ${AJUDA_LABEL[m.nivel_ajuda]}` : "";
}

function regulacaoDescricao(m: MomentoPsicomotor): string {
  if (!m.estado_antes && !m.estado_depois) return "";
  const a = m.estado_antes ? ESTADO_REGULACAO_LABEL[m.estado_antes] : "?";
  const dpo = m.estado_depois ? ESTADO_REGULACAO_LABEL[m.estado_depois] : "?";
  return ` · ${a} → ${dpo}`;
}
